import { useEffect, useMemo, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useColumnOrder, useGlobalFilter, usePagination, useSortBy, useTable, Row, TableInstance } from "react-table";
import Cookies from "js-cookie";

import usePermissions from "hooks/usePermissions";
import { updateShipmentIdAndTitle } from "store/store";
import { useGetAllShipments } from "api/queries/shipments/useGetAllShipments";
import { useGetShipmentList } from "api/queries/shipments/useGetShipmentList";
import shipmentFilter from "helpers/shipmentFilter";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";
import { ShipmentAdditionalColumns } from "../../../../constants";

const DISPLAY_STATUS = {
  ALL: "",
  BOOKED: "Booked",
  SHIPPING: "In transit",
  DELAYED: "Delayed",
  DELIVERED: "Delivered",
  INCOMPLETE: "Incomplete",
  ARCHIVED: "archived",
};

const shipmentDataPlaceholder = {
  all: [],
  [DISPLAY_STATUS.BOOKED]: [],
  [DISPLAY_STATUS.SHIPPING]: [],
  [DISPLAY_STATUS.DELAYED]: [],
  [DISPLAY_STATUS.DELIVERED]: [],
  [DISPLAY_STATUS.INCOMPLETE]: [],
};

const shipmentDataSelector = (data: any) => ({
  all: data || [],
  [DISPLAY_STATUS.BOOKED]: data?.filter((item: any) => item.display_status?.value === "Booked") || [],
  [DISPLAY_STATUS.SHIPPING]: data?.filter((item: any) => item.display_status?.value === "In transit") || [],
  [DISPLAY_STATUS.DELAYED]: data?.filter((item: any) => item.display_status?.value === "Delayed") || [],
  [DISPLAY_STATUS.DELIVERED]: data?.filter((item: any) => item.display_status?.value === "Delivered") || [],
  [DISPLAY_STATUS.INCOMPLETE]: data?.filter((item: any) => item.display_status?.value === "Incomplete") || [],
});

type UseCommonShipmentListingOptions = {
  getShipmentColumns: Function;
  columnOrder?: string[];
  archiveFilters?: (instance: TableInstance<object>) => any;
};

function useCommonShipmentListing(options: UseCommonShipmentListingOptions) {
  const { getShipmentColumns, columnOrder, archiveFilters } = options;
  const intl = useIntl();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { actions }: any = useStateMachine({ updateShipmentIdAndTitle });
  const { persistedColumnOrder = columnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "shipment" });

  const status: string = searchParams.get("display_status") || "";

  const queryPage = +(searchParams.get("page") || 0);
  const { data = shipmentDataPlaceholder, isLoading }: any = useGetAllShipments({
    select: shipmentDataSelector,
  });
  const [archiveData, setArchiveData] = useState<any>([]);
  const [totalArchiveData, setTotalArchiveData] = useState(0);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string>("");
  const [showGuestAccessDialog, setShowGuestAccessDialog] = useState<boolean>(false);

  useEffect(() => {
    if (Cookies.get("is_guest") !== "true")
      if (!JSON.parse(permissions || "{}").permissions.tracking.view_shipments) navigate("/access-denied");
  }, []);

  const handleOpenGuestAccessDialog = (shipmentId: string) => {
    setSelectedShipmentId(shipmentId);
    setShowGuestAccessDialog(true);
  };

  const columns = useMemo(() => getShipmentColumns(intl, handleOpenGuestAccessDialog), []);

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("shipments_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("shipments_hidden_columns", JSON.stringify(ShipmentAdditionalColumns));
    return ShipmentAdditionalColumns;
  };

  const instance = useTable(
    {
      initialState: {
        pageSize: +(Cookies.get("listing_sizeperpage") || 10),
        pageIndex: queryPage ? queryPage - 1 : 0,
        hiddenColumns: getHiddenColumns(),
        columnOrder: persistedColumnOrder,
        globalFilter: {},
      },
      columns,
      data: status === DISPLAY_STATUS.ARCHIVED ? archiveData : data[!status ? "all" : status],
      autoResetHiddenColumns: false,
      autoResetGlobalFilter: false,
      ...(status !== DISPLAY_STATUS.ARCHIVED && { globalFilter: shipmentFilter }),
      manualGlobalFilter: status === DISPLAY_STATUS.ARCHIVED,
      manualSortBy: status === DISPLAY_STATUS.ARCHIVED,
      manualPagination: status === DISPLAY_STATUS.ARCHIVED,
      ...(status === DISPLAY_STATUS.ARCHIVED && {
        pageCount: Math.ceil(totalArchiveData / +(Cookies.get("listing_sizeperpage") || 10)),
      }),
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useColumnOrder,
  );

  const { data: queryData, isLoading: archiveLoading }: any = useGetShipmentList(
    {
      page_size: instance.state.pageSize,
      page: instance.state.pageIndex + 1,
      ...(instance.state.sortBy[0] && {
        sort_by: `${instance.state.sortBy[0].id}:${instance.state.sortBy[0].desc ? "DESC" : "ASC"}`,
      }),
      display_status: DISPLAY_STATUS.ARCHIVED,
      properties: ShipmentAdditionalColumns.join(","),
      ...(instance?.state?.sortBy?.[0]?.id === "location" && {
        sort_by: `payment_location:${instance?.state?.sortBy?.[0]?.desc ? "DESC" : "ASC"}`,
      }),
      ...(instance.state.globalFilter.query?.length && { query: instance.state.globalFilter.query }),
      ...(instance.state.globalFilter.display_status?.length && {
        display_status: instance.state.globalFilter.display_status.join(","),
      }),
      ...(instance.state.globalFilter.freight_mode?.length && {
        freight_modes: instance.state.globalFilter.freight_mode.join(","),
      }),
      ...(instance.state.globalFilter.pickup_city?.length && { pickup_city: instance.state.globalFilter.pickup_city }),
      ...(instance.state.globalFilter.delivery_city?.length && { delivery_city: instance.state.globalFilter.delivery_city }),
      ...(archiveFilters && archiveFilters(instance)),
    },
    { enabled: status === DISPLAY_STATUS.ARCHIVED },
  );

  useEffect(() => {
    if (queryData) {
      setArchiveData(queryData?.results);
      setTotalArchiveData(queryData?.count);
    }
  }, [queryData]);

  useEffect(() => {
    instance.gotoPage(queryPage ? queryPage - 1 : 0);
  }, [queryPage]);

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  const handleCloseManageAccessDialog = () => {
    setShowGuestAccessDialog(false);
  };

  const handleRowClick = async (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.shipment_reference;
    const title = row.original?.shipment_title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    if (e.ctrlKey || e.metaKey) window.open(`/shipment/${reference}/tracking`, "_blank");
    else navigate(`/shipment/${reference}/tracking`, { state: { data: row.original?.created_by } });
  };

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
      ...((eventKey === DISPLAY_STATUS.ARCHIVED || status === DISPLAY_STATUS.ARCHIVED) && {
        payment_location: [],
        freight_mode: [],
      }),
    }));
  };

  return {
    instance,
    data,
    totalArchiveData,
    isLoading,
    selectedShipmentId,
    showGuestAccessDialog,
    archiveLoading,
    handleCloseManageAccessDialog,
    handleRowClick,
    handleStatusChange,
    DISPLAY_STATUS,
    onColumnOrderChange,
  };
}

export default useCommonShipmentListing;
